import { useState } from 'react';

import { updateStatus } from '@lumapps/contents/api/contents';
import { LegacyContentStatus } from '@lumapps/contents/types';
import type { ContentSave, ContentUpdate } from '@lumapps/designer-content-editor/types';
import { useNotification } from '@lumapps/notifications/hooks/useNotifications';
import { GLOBAL, useTranslate } from '@lumapps/translations';
import { useBooleanState } from '@lumapps/utils/hooks/useBooleanState';

import type { ContentScheduleDialogProps } from '../components/ContentScheduleDialog';
import type { ContentSummaryDialogProps } from '../components/ContentSummaryDialog';
import { DESIGNER } from '../keys';

export const useContentScheduling = ({
    updateContent,
    openSummaryDialog,
    publishContent,
    saveContent,
    contentId,
}: {
    contentId?: string;
    updateContent: ContentUpdate;
    saveContent: ContentSave;
    publishContent: () => void;
    openSummaryDialog: (props: Pick<ContentSummaryDialogProps, 'onConfirm' | 'publicationSourceType'>) => void;
}) => {
    const [isScheduleDialogOpen, , closeScheduleDialog, openDialog] = useBooleanState(false);
    const [shouldShowSummaryDialog, setShouldShowSummaryDialog] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const { error, success } = useNotification();
    const { translateKey } = useTranslate();

    const openScheduleDialog =
        ({ showSummary }: { showSummary: boolean }) =>
        () => {
            setShouldShowSummaryDialog(showSummary);
            openDialog();
        };

    const handleConfirm: ContentScheduleDialogProps['onConfirm'] = async ({ startDate, endDate }) => {
        const startDateAsDate = startDate ? new Date(startDate) : undefined;

        const endDateAsDate = endDate ? new Date(endDate) : undefined;

        if (shouldShowSummaryDialog) {
            // We only update the temporary values for start and end date at first.
            // Real values will be updated on confirming the publish.
            updateContent({
                content: {
                    startDateInput: {
                        date: startDate,
                        hour: startDateAsDate && startDateAsDate.getHours(),
                        min: startDateAsDate && startDateAsDate.getMinutes(),
                    },
                    endDateInput: {
                        date: endDate,
                        hour: endDateAsDate && endDateAsDate.getHours(),
                        min: endDateAsDate && endDateAsDate.getMinutes(),
                    },
                },
                isPartial: true,
            });

            openSummaryDialog({
                publicationSourceType: 'schedule',
                onConfirm: () => {
                    // Before publishing, we update the startDate and endDate
                    updateContent({
                        content: {
                            startDate,
                            endDate,
                        },
                        isPartial: true,
                    });
                    publishContent();
                },
            });
            closeScheduleDialog();
        } else {
            setIsLoading(true);
            updateContent({
                content: {
                    startDate,
                    endDate,
                    startDateInput: {
                        date: startDate,
                        hour: startDateAsDate && startDateAsDate.getHours(),
                        min: startDateAsDate && startDateAsDate.getMinutes(),
                    },
                    endDateInput: {
                        date: endDate,
                        hour: endDateAsDate && endDateAsDate.getHours(),
                        min: endDateAsDate && endDateAsDate.getMinutes(),
                    },
                },
                isPartial: true,
            });
            try {
                await publishContent();
                closeScheduleDialog();
            } catch (error) {
                // Error notification is handled by the legacy publish callback.
            } finally {
                setIsLoading(false);
            }
        }
    };

    const unscheduleContent = async () => {
        if (!contentId) {
            return;
        }

        try {
            const { data: updatedContent } = await updateStatus({ contentId, status: LegacyContentStatus.draft });
            updateContent({ content: updatedContent, isPartial: false });
            await saveContent({ shouldNotTriggerLegacySuccess: true });
            success({ content: translateKey(DESIGNER.UNSCHEDULE_SUCCESS_MESSAGE) });
        } catch (err) {
            error({ content: translateKey(GLOBAL.GENERIC_ERROR) });
        }
    };

    return {
        openScheduleDialog,
        unscheduleContent,
        dialogProps: {
            isLoading,
            isOpen: isScheduleDialogOpen,
            onClose: closeScheduleDialog,
            onConfirm: handleConfirm,
        },
    };
};
